// Perfil segmentado do onboarding (4 segmentos SEPARADOS: médico, residente, estudante, outro profissional).
// Módulo puro, usado pelo navegador (telas) e pelo servidor (functions/lib/onboarding-profile.js), para a
// mesma regra valer nos dois lados. Nada aqui coleta dado de saúde, de paciente ou resultado de cálculo.
//
// Estrutura: tela "motivo" (aquecimento, NÃO pontua) → 3 telas que pontuam (segmento, contexto, necessidade)
// → tela "amostra" (fechamento, NÃO pontua). A primeira e a última ficam fora da regra de qualificação para
// absorver o clique apressado de quem só quer chegar ao produto.

export const SEGMENTOS = ['medico', 'residente', 'estudante', 'outro_profissional'];
export const SCORED_SCREENS = ['segmento', 'contexto', 'necessidade'];
export const ALL_SCREENS = ['motivo', 'segmento', 'contexto', 'necessidade', 'amostra'];

// Piso de leitura (ms) abaixo do qual uma resposta não vale como lida. Ponto de partida do plano (3 s);
// calibrar com os tempos reais no PostHog. Parametrizável no servidor por ONBOARDING_MIN_READ_MS.
export const MIN_READ_MS_DEFAULT = 3000;
export const MIN_READ_MS_RANGE = [1000, 10000];
// Straightlining só conta quando as três respostas têm a mesma posição E alguma delas foi rápida (abaixo
// de fator × piso). Assim não punimos quem leu com calma e por acaso escolheu a mesma posição.
export const STRAIGHTLINING_FAST_FACTOR = 2;

const opt = (value, label) => ({ value, label });
const TAIL_VALUES = new Set(['outro', 'outra']);

export const MOTIVO = {
  id: 'motivo',
  scored: false,
  question: 'O que te trouxe ao MiniMental hoje?',
  options: [
    opt('calcular_escala', 'Calcular uma escala ou score'),
    opt('consultar_referencia', 'Consultar uma referência'),
    opt('conhecer_o_site', 'Conhecer o site'),
    opt('outro', 'Outro motivo'),
  ],
};

export const SEGMENTO = {
  id: 'segmento',
  scored: true,
  question: 'Quem é você?',
  options: [
    opt('medico', 'Médico(a)'),
    opt('residente', 'Residente'),
    opt('estudante', 'Estudante de medicina'),
    opt('outro_profissional', 'Outro profissional de saúde'),
  ],
};

export const CONTEXTO = {
  medico: {
    question: 'Onde você mais atua?',
    options: [opt('plantao', 'Plantão ou emergência'), opt('consultorio', 'Consultório ou ambulatório'), opt('hospital', 'Enfermaria ou hospital'), opt('outro', 'Outro')],
  },
  residente: {
    question: 'Qual a sua área de residência?',
    options: [opt('clinica', 'Clínica médica'), opt('pediatria', 'Pediatria'), opt('cirurgia', 'Cirurgia'), opt('emergencia', 'Emergência'), opt('go', 'Ginecologia e obstetrícia'), opt('outra', 'Outra')],
  },
  estudante: {
    question: 'Em que fase você está?',
    options: [opt('ciclo_basico', 'Ciclo básico'), opt('ciclo_clinico', 'Ciclo clínico'), opt('internato', 'Internato'), opt('preparando_residencia', 'Preparando para a residência')],
  },
  outro_profissional: {
    question: 'Qual a sua área?',
    options: [opt('enfermagem', 'Enfermagem'), opt('farmacia', 'Farmácia'), opt('outra', 'Outra')],
  },
};

export const NECESSIDADE = {
  medico: {
    question: 'O que mais te ajudaria hoje?',
    options: [opt('conduta', 'Conduta e prescrição rápida'), opt('calculadoras', 'Calculadoras e escalas'), opt('presenca_digital', 'Presença digital e captar pacientes')],
  },
  residente: {
    question: 'O que mais te ajudaria hoje?',
    options: [opt('conduta', 'Conduta e prescrição rápida'), opt('calculadoras', 'Calculadoras e escalas'), opt('presenca_digital', 'Presença digital e captar pacientes')],
  },
  estudante: {
    question: 'O que mais te ajudaria hoje?',
    options: [opt('aprender_conduta', 'Aprender conduta com segurança'), opt('calculadoras', 'Calculadoras e escalas'), opt('prova_residencia', 'Prova de residência')],
  },
  outro_profissional: {
    question: 'O que mais te ajudaria hoje?',
    options: [opt('conduta', 'Conduta e prescrição rápida'), opt('calculadoras', 'Calculadoras e escalas'), opt('outra', 'Outra')],
  },
};

export const AMOSTRA = {
  id: 'amostra',
  scored: false,
  question: 'Quer receber uma amostra gratuita do Kit do Plantonista no seu e-mail?',
  options: [opt('sim', 'Sim, quero receber'), opt('nao', 'Agora não')],
};

/** Tela (pergunta + opções) para o id e as respostas já dadas. Retorna null se faltar o segmento. */
export function getScreen(id, answers = {}) {
  if (id === 'motivo') return MOTIVO;
  if (id === 'segmento') return SEGMENTO;
  if (id === 'amostra') return AMOSTRA;
  const seg = answers.segmento;
  if (!SEGMENTOS.includes(seg)) return null;
  if (id === 'contexto') return { id, scored: true, ...CONTEXTO[seg] };
  if (id === 'necessidade') return { id, scored: true, ...NECESSIDADE[seg] };
  return null;
}

export function isAllowedAnswer(screenId, value, answers = {}) {
  const screen = getScreen(screenId, answers);
  return Boolean(screen && screen.options.some((o) => o.value === value));
}

function mulberry32(seed) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function hashSeed(text) {
  let h = 2166136261;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/**
 * Embaralha as opções de forma determinística (mesma semente → mesma ordem) para reduzir viés de posição.
 * "Outro"/"Outra" fica sempre por último.
 */
export function shuffleOptions(options, seedText) {
  const head = options.filter((o) => !TAIL_VALUES.has(o.value));
  const tail = options.filter((o) => TAIL_VALUES.has(o.value));
  const rand = mulberry32(hashSeed(String(seedText || 'seed')));
  for (let i = head.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [head[i], head[j]] = [head[j], head[i]];
  }
  return [...head, ...tail];
}

export function clampMinReadMs(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return MIN_READ_MS_DEFAULT;
  return Math.min(MIN_READ_MS_RANGE[1], Math.max(MIN_READ_MS_RANGE[0], Math.round(n)));
}

const isMs = (v) => typeof v === 'number' && Number.isFinite(v) && v >= 0 && v <= 3_600_000;
const isPos = (v) => Number.isInteger(v) && v >= 0 && v <= 9;

/**
 * Avalia o perfil: respostas permitidas para o segmento, tempo de leitura por tela e straightlining.
 * Respostas fora das opções do segmento são descartadas (inconsistência não passa).
 */
export function assessProfile({ answers = {}, timings = {}, positions = {}, minReadMs = MIN_READ_MS_DEFAULT } = {}) {
  const floor = clampMinReadMs(minReadMs);
  const clean = {};
  if (isAllowedAnswer('motivo', answers.motivo, answers)) clean.motivo = answers.motivo;
  if (isAllowedAnswer('segmento', answers.segmento, answers)) clean.segmento = answers.segmento;
  if (clean.segmento) {
    const withSeg = { segmento: clean.segmento };
    if (isAllowedAnswer('contexto', answers.contexto, withSeg)) clean.contexto = answers.contexto;
    if (isAllowedAnswer('necessidade', answers.necessidade, withSeg)) clean.necessidade = answers.necessidade;
  }
  if (isAllowedAnswer('amostra', answers.amostra, answers)) clean.amostra = answers.amostra;

  const lidas = {};
  for (const id of SCORED_SCREENS) lidas[id] = Boolean(clean[id]) && isMs(timings[id]) && timings[id] >= floor;

  const pos = SCORED_SCREENS.map((id) => positions[id]);
  const allAnswered = SCORED_SCREENS.every((id) => Boolean(clean[id]));
  const samePosition = allAnswered && pos.every(isPos) && pos.every((p) => p === pos[0]);
  const anyFast = SCORED_SCREENS.some((id) => !isMs(timings[id]) || timings[id] < floor * STRAIGHTLINING_FAST_FACTOR);
  const straightlining = samePosition && anyFast;

  const perfilValido = Boolean(clean.segmento && clean.contexto) && lidas.segmento && lidas.contexto && !straightlining;
  return { answers: clean, lidas, straightlining, perfilValido, minReadMs: floor };
}

export function sanitizeEngajamento(raw) {
  const r = raw && typeof raw === 'object' ? raw : {};
  return { clicou_oferta: r.clicou_oferta === true, sessao_retorno: r.sessao_retorno === true };
}

/**
 * Regra v1 de LeadQualificado: perfil com respostas lidas (telas 1 e 2 válidas, sem straightlining) E sinal de
 * interesse (clicou na oferta do segmento OU voltou em outro dia). As telas "motivo" e "amostra" não entram.
 * Obs.: "usou 2 ou mais ferramentas" ficou de fora de propósito: o repositório decidiu não gravar o uso da
 * calculadora no navegador, e o servidor não guarda esse histórico.
 */
export function qualify(assessment, engajamento) {
  const eng = sanitizeEngajamento(engajamento);
  return Boolean(assessment?.perfilValido && (eng.clicou_oferta || eng.sessao_retorno));
}

/** Propriedades que podem ir ao Tracking Hub/Meta: só perfil profissional, nunca texto livre ou dado de saúde. */
export function hubProfileProps(assessment) {
  const a = assessment?.answers || {};
  const out = {};
  if (a.segmento) out.segmento = a.segmento;
  if (a.contexto) out.contexto = a.contexto;
  if (a.necessidade) out.necessidade = a.necessidade;
  return out;
}
